import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { StudentAnswerService } from '@app/infrastructure/core/services/api/student-answer.service';
import { NotificationService } from '@app/infrastructure/core/services/notification.service';
import { QuizAnswerState } from '@app/infrastructure/core/services/state/quiz-answer-state';
import {
  IStudentAnswerRequestDTO,
  StudentAnswerDTO,
  StudentAnswerRequestDTO,
} from '@app/infrastructure/models/answer';
import {
  IQuizBlockDTO,
  QuizBlockDTO,
  QuizTypes,
} from '@app/infrastructure/models/quiz-block';
import {
  IStudentAssignmentDTO,
  StudentAssignmentDTO,
} from '@app/infrastructure/models/student-assignment';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
  selector: 'app-shared-student-quiz-block',
  templateUrl: './student-quiz-block.component.html',
  styleUrls: ['./student-quiz-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SharedStudentQuizBlockComponent implements OnInit, OnDestroy {
  @Input() quiz: IQuizBlockDTO = new QuizBlockDTO();
  @Input() studentId: string;
  @Input() studentAssignment: StudentAssignmentDTO;
  @Input() dayBlockId: number;
  @Input() answer: StudentAnswerDTO;
  @Input() booleanSaved: boolean = false;
  @Input() textSaved: boolean = false;
  @Input() optionSaved: boolean = false;
  @Input() unanswered: boolean = true;
  @Output() emitSubmitAnswer = new EventEmitter<boolean>();
  @Output() emitResults = new EventEmitter<IStudentAssignmentDTO>();
  public dayForm: UntypedFormGroup = new UntypedFormGroup({});
  public QUIZTYPE = QuizTypes;
  public option: string = '';
  public freeAnswer: string = '';
  public trueOrFalse: string = '';
  public answerList: any[];
  public ordinal(str: string): string {
    return String.fromCharCode(parseInt(str, 10) + 65);
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private studentAnswerService: StudentAnswerService,
    private quizAnswerState: QuizAnswerState,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.setAnswers(this.quiz.quizType);
    this.dayForm = new UntypedFormGroup({
      trueOrFalse: new UntypedFormControl({
        value: this.trueOrFalse,
        disabled: this.studentAssignment?.completedAt,
      }),
      option: new UntypedFormControl({
        value: this.option,
        disabled: this.studentAssignment?.completedAt,
      }),
      freeAnswer: new UntypedFormControl({
        value: this.freeAnswer,
        disabled: this.studentAssignment?.completedAt,
      }),
    });
    const subscription = this.dayForm.valueChanges
      .pipe(
        filter(() => this.dayForm.valid),
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        this.saveQuiz(this.quiz.quizType);
      });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public setAnswers(quizType: string): void {
    if (this.answer) {
      switch (quizType) {
        case QuizTypes.MULTIPLECHOICE:
          this.option = this.answer.studentOptionsAnswer;
          break;
        case QuizTypes.BOOLEANCHOICE:
          this.trueOrFalse = this.answer.studentTrueOrFalseAnswer
            ? 'true'
            : 'false';
          break;
        case QuizTypes.FREEANSWER:
          this.freeAnswer = this.answer.studentTextAnswer;
          break;
        default:
          break;
      }
    }
    this.answerList = this.quizAnswerState.getAnswer(this.dayBlockId);
    this.answerList?.forEach((item) => {
      if (item.dayBlockId === this.dayBlockId) {
        switch (quizType) {
          case QuizTypes.MULTIPLECHOICE:
            this.option = item.value;
            break;
          case QuizTypes.BOOLEANCHOICE:
            this.trueOrFalse = item.value;
            break;
          case QuizTypes.FREEANSWER:
            this.freeAnswer = item.value;
            break;
          default:
            break;
        }
      }
    });
  }

  public onChangeOption(answer: string): void {
    this.quizAnswerState.storeAnswer(answer, this.dayBlockId);
  }

  public onChangeTrueFalse(value: string): void {
    this.quizAnswerState.storeAnswer(value, this.dayBlockId);
  }

  public onChangeFreeAnswer(value: string): void {
    this.quizAnswerState.storeAnswer(value, this.dayBlockId);
  }

  saveQuiz(quizType: string): void {
    if (this.answer?.id) {
      this.updateAnswer(quizType);
    } else {
      this.createAnswer(quizType);
    }
  }

  setBlockToAnswered(answer) {
    switch (answer.quizType) {
      case QuizTypes.MULTIPLECHOICE:
        const optionControl = 'option';
        this.dayForm.controls[optionControl].enable();
        this.optionSaved = false;
        break;
      case QuizTypes.BOOLEANCHOICE:
        const booleanControl = 'trueOrFalse';
        this.dayForm.controls[booleanControl].enable();
        this.booleanSaved = false;
        break;
      default:
        break;
    }
    this.emitResults.emit(answer);
  }

  setQuizToAnswered(answer) {
    this.answer = answer;
    this.emitSubmitAnswer.emit(false);
    this.emitResults.emit(answer);
  }

  private updateAnswer(quizType: string): void {
    switch (quizType) {
      case QuizTypes.MULTIPLECHOICE:
        const option = this.dayForm.get('option').value;
        if (!option.length) {
          this.notificationService.showError(['Please select an answer.']);
          return;
        } else {
          this.answer.studentOptionsAnswer = option;
          this.answer.quizType = QuizTypes.MULTIPLECHOICE;
          const optionControl = 'option';
          this.optionSaved = true;
        }
        break;
      case QuizTypes.BOOLEANCHOICE:
        const trueOrFalse = this.dayForm.get('trueOrFalse').value;
        if (!trueOrFalse.length) {
          this.notificationService.showError(['Please select an answer.']);
          return;
        } else {
          this.answer.studentTrueOrFalseAnswer = trueOrFalse;
          this.answer.quizType = QuizTypes.BOOLEANCHOICE;
          const booleanControl = 'trueOrFalse';
          this.booleanSaved = true;
        }
        break;
      case QuizTypes.FREEANSWER:
        const freeAnswer = this.dayForm.get('freeAnswer').value;
        if (!freeAnswer.length) {
          this.notificationService.showError(['Please enter an answer.']);
          return;
        } else {
          this.answer.studentTextAnswer = freeAnswer;
          this.answer.quizType = QuizTypes.FREEANSWER;
          const text = 'freeAnswer';
          this.textSaved = true;
        }
        break;
      default:
        break;
    }
    this.studentAnswerService
      .updateStudentAnswer(
        this.studentAssignment?.id,
        this.studentId,
        this.answer,
      )
      .subscribe((answer) => this.setQuizToAnswered(answer));
  }

  private createAnswer(quizType: string): void {
    const freeAnswer = this.dayForm.get('freeAnswer').value;
    const trueOrFalse = this.dayForm.get('trueOrFalse').value;
    const option = this.dayForm.get('option').value;

    const payload: IStudentAnswerRequestDTO = new StudentAnswerRequestDTO({
      studentId: this.studentId,
      studentAssignmentId: this.studentAssignment?.id,
      dayBlockId: this.dayBlockId,
      studentOptionsAnswer: null,
      studentTrueOrFalseAnswer: null,
      studentTextAnswer: null,
      quizType: null,
    });
    switch (quizType) {
      case QuizTypes.MULTIPLECHOICE:
        if (!option.length) {
          this.notificationService.showError(['Please select an answer.']);
          return;
        } else {
          payload.studentOptionsAnswer = option;
          payload.quizType = QuizTypes.MULTIPLECHOICE;
          const optionControl = 'option';
          this.optionSaved = true;
        }
        break;
      case QuizTypes.BOOLEANCHOICE:
        if (!trueOrFalse.length) {
          this.notificationService.showError(['Please select an answer.']);
          return;
        } else {
          payload.studentTrueOrFalseAnswer = trueOrFalse;
          payload.quizType = QuizTypes.BOOLEANCHOICE;
          const booleanControl = 'trueOrFalse';
          this.dayForm.controls[booleanControl];
          this.booleanSaved = true;
        }
        break;
      case QuizTypes.FREEANSWER:
        if (!freeAnswer.length) {
          this.notificationService.showError(['Please enter an answer.']);
          return;
        } else {
          payload.studentTextAnswer = freeAnswer;
          payload.quizType = QuizTypes.FREEANSWER;
          const text = 'freeAnswer';
          this.dayForm.controls[text];
          this.textSaved = true;
        }
        break;
      default:
        break;
    }
    if (this.studentAssignment) {
      this.studentAnswerService
        .createStudentAnswer(
          this.studentAssignment?.id,
          this.studentId,
          payload,
        )
        .subscribe((answer) => this.setQuizToAnswered(answer));
    } else {
      this.setBlockToAnswered(payload);
    }
  }
}
